import type { Flow } from '~/models/flow.model';
import type { Page } from '~/models/page.model';
import type { User } from '~/models/user.model';
import { IEnrichedFlowTemplate, TBlockType } from '~/types/model.types';
import { ContractBlock } from '~/models/blocks/contract-block.model';
import { checkLimitations } from '~/components/Builder/Common/CreationLimitations/creationLimitations';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import { PickDeep } from 'type-fest';
import { TBlock } from '~/types/builder.types';
import { TextBlock } from '~/models/blocks/text-block.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { ImageBlock } from '~/models/blocks/image-block.model';

export type PageLike = Pick<
    Page,
    | 'page_id'
    | 'order'
    | 'actionBlock'
    | 'isInvoicePage'
    | 'contractBlock'
    | 'isSessionSelectionPage'
>;

export type FlowLike = Pick<
    Flow,
    | 'isLeadForm'
    | 'pages'
    | 'title'
    | 'pageWithPay'
    | 'hasChargeBlock'
    | 'pagesMap'
    | 'hasInvoiceBlock'
    | 'pagesWithServiceSelection'
    | 'hasPaidPayments'
    | 'hasScheduleBlock'
    | 'hasSessionSelectionBlock'
    | 'pageWithInvoice'
    | 'pageWithScheduler'
    | 'firstContractWithServicesRecapsPage'
    | 'sessionSelectionBlock'
    | 'uploadQuestionBlocksCount'
>;

export type ContainerBlockLike = {
    type: Extract<TBlockType, 'container_block'>;
} & Pick<ContainerBlock, 'childBlocks' | 'children'>;

type TextBlockLike = {
    type: Extract<TBlockType, 'text_block'>;
} & Pick<TextBlock, 'content'>;

type QuestionBlockLike = {
    type: Extract<TBlockType, 'question_block'>;
} & PickDeep<Pick<QuestionBlock, 'question'>, 'question.question_type'>;

type ContractBlockLike = {
    type: Extract<TBlockType, 'contract_block'>;
} & Pick<ContractBlock, 'hasServicesRecaps'>;

type InvoiceBlockLike = {
    type: Extract<TBlockType, 'invoice_container_block'>;
} & Pick<InvoiceBlock, 'isClientPaymentPlan'>;

export type ServiceSelectionBlockLike = {
    type: Extract<TBlockType, 'service_selection_block'>;
} & Pick<ServiceSelectionBlock, 'service_selection_type'>;

type ImageBlockLike = {
    type: Extract<TBlockType, 'image_block'>;
} & Pick<ImageBlock, 'isCanvaBlock'>;

type OtherBlockLike = {
    type: Exclude<
        TBlockType,
        | TextBlockLike['type']
        | ContractBlockLike['type']
        | ServiceSelectionBlockLike['type']
        | ContainerBlockLike['type']
        | QuestionBlockLike['type']
        | InvoiceBlockLike['type']
    >;
};

export type BlockLike =
    | TextBlockLike
    | ContractBlockLike
    | ServiceSelectionBlockLike
    | ContainerBlockLike
    | QuestionBlockLike
    | InvoiceBlockLike
    | ImageBlockLike
    | OtherBlockLike;

// You can add source to your limitations in order to be able to return the right text. This field is not supposed
// to be used for the actual check but just to decide which text to return.
// The limitation is supposed to be checked without context but just with plain data to keep the check pure.
export type SourceForMessage = 'reuse';

export type LimitationContext = {
    user: User;
    targetFlow: FlowLike;
    targetPage: PageLike;
    targetBlock: BlockLike;
    sourceFlowTemplate: IEnrichedFlowTemplate;
    sourcePage: PageLike;
    sourceBlock: BlockLike;
    sourceForMessage: SourceForMessage;
    // Represents children of a container block whose flow not yet been loaded in the flowStore.
    // Useful in scenarios like copying a container block from one flow and pasting it to another
    // where the flow to which the container block belongs may not be loaded in the flowStore.
    notLoadedFlowChildren: TBlock[];
};

export type LimitationsKeyType = TBlockType | 'all';
export type MandatoryContextProps = Pick<LimitationContext, 'sourceBlock'>;
export type LimitationProps<
    TMandatory extends keyof LimitationContext,
    TOptional extends keyof LimitationContext = never
> = MandatoryContextProps &
    Pick<LimitationContext, TMandatory> &
    Partial<Pick<LimitationContext, TOptional>>;

export type CreationLimitation = Allowed | Restricted | Disabled | Hidden;

export type Allowed = {
    status: 'allowed';
};
export const AsAllowed: Allowed = { status: 'allowed' };

export type Restricted = {
    status: 'restricted';
    reason: {
        short: string;
        long: string;
        cta: string;
        rejectCta?: string;
    };
};

export function AsRestricted<R extends Restricted>(
    restriction: Omit<R, 'status'>
) {
    return {
        status: 'restricted',
        ...restriction
    } as R;
}

export type Disabled = {
    status: 'disabled';
    reason: string;
};
export const AsDisabled = (reason: string): Disabled => ({
    status: 'disabled',
    reason
});

export type Hidden = {
    status: 'hidden';
    reason: string;
};

export const AsHidden = (reason: string): Hidden => ({
    status: 'hidden',
    reason
});

export type LimitationPredicate = (
    props: Partial<LimitationProps<keyof LimitationContext>>
) => CreationLimitation;

export type PossibleLimitations<
    T extends Partial<Record<string, ReadonlyArray<LimitationPredicate>>>,
    K extends LimitationsKeyType
> = ReturnType<typeof checkLimitations<K, T>>;

export type ExtractRestrictions<T> = Exclude<
    T,
    Exclude<CreationLimitation, Restricted>
>;

export type PossibleRestrictions<
    T extends Partial<Record<string, ReadonlyArray<LimitationPredicate>>>,
    K extends LimitationsKeyType
> = ExtractRestrictions<PossibleLimitations<T, K>>;
